/**
 * Hardware counts per type, scoped to a system or an organization.
 *
 * The hardware list shows quick-access toggles for the most used hardware types,
 * which needs a count of hardware per type within the current scope. The GraphQL
 * API has no grouped aggregate for that, so this asks for one aliased
 * `listHardware(...) { count }` per type in a single request. The type list is
 * short — only the types actually in use in the scope — so this stays one round
 * trip with a handful of COUNTs.
 *
 * When a server-side grouped aggregate lands (ant), this hook is the only thing
 * that needs to change: callers just consume `{ name, count }` pairs.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getGraphQLClient } from '../../../../api/graphql';

export interface HardwareTypeCount {
  name: string;
  count: number;
}

interface UseHardwareTypeCountsParams {
  /** Type names to count, e.g. ['bike', 'lock']. */
  typeNames: string[];
  /** System-scoped count (system view). Takes precedence over organizationId. */
  systemId?: string;
  /** Organization-scoped count (organization view). */
  organizationId?: string;
  enabled?: boolean;
}

/**
 * Builds a query with one aliased count per type. Aliases are positional (`t0`,
 * `t1`, ...) and filters are passed as variables, so type names never end up
 * interpolated into the document.
 */
export const buildHardwareTypeCountsQuery = (typeCount: number): string => {
  const params = Array.from(
    { length: typeCount },
    (_, index) => `$f${index}: HardwareFilterInput`
  ).join(', ');
  const fields = Array.from(
    { length: typeCount },
    (_, index) => `  t${index}: listHardware(filter: $f${index}, first: 1) { count }`
  ).join('\n');

  return `query HardwareTypeCounts(${params}) {\n${fields}\n}`;
};

export function useHardwareTypeCounts({
  typeNames,
  systemId,
  organizationId,
  enabled = true,
}: UseHardwareTypeCountsParams) {
  // Stable across re-renders so the query key doesn't churn on every render.
  const names = useMemo(() => [...typeNames].sort(), [typeNames]);

  const scopeFilter = useMemo(() => {
    if (systemId) return { systemId: { eq: systemId } };
    if (organizationId) return { organizationId: { eq: organizationId } };
    return null;
  }, [systemId, organizationId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['hardwareTypeCounts', systemId ?? null, organizationId ?? null, names],
    enabled: enabled && Boolean(scopeFilter) && names.length > 0,
    queryFn: async (): Promise<HardwareTypeCount[]> => {
      // buildHardwareTypeCountsQuery(0) would emit `query X() {}` — empty variable
      // parens and an empty selection set, which is a syntax error. `enabled`
      // normally prevents this, but refetch() bypasses enabled, so guard here too.
      if (names.length === 0) return [];

      const variables = Object.fromEntries(
        names.map((name, index) => [
          `f${index}`,
          { ...scopeFilter, hardwareType: { name: { eq: name } } },
        ])
      );

      const response = await getGraphQLClient().request<
        Record<string, { count: number | null } | null>
      >(buildHardwareTypeCountsQuery(names.length), variables);

      return names.map((name, index) => ({
        name,
        count: response?.[`t${index}`]?.count ?? 0,
      }));
    },
  });

  return {
    data: (data as HardwareTypeCount[] | undefined) ?? [],
    isLoading,
    error,
  };
}

/**
 * Picks the most used type names, highest count first. Types with no hardware are
 * dropped. Ties break alphabetically so the toggles don't reshuffle between
 * loads.
 */
export function topHardwareTypeNames(
  counts: HardwareTypeCount[],
  limit: number
): string[] {
  return [...counts]
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, limit)
    .map((item) => item.name);
}
